import React from 'react'
import { Box, Link } from '@mui/material'

import Txt from './Txt'
import Button from './Button'
import TextField from './TextField'
import Dialog, { DialogButtonRow } from './Dialog'
import { handleMutationError } from '../utils/errors'
import { Gewerbeamt, useGewerbeamt } from '../utils/gewerbeauskunft'
import type { Restaurant } from '../utils/restaurant'
import sendMessage from '../mutations/sendMessage'
import createRestaurantMessage from '../mutations/createRestaurantMessage'
import {
	COMPANY_CITY,
	COMPANY_EMAIL,
	COMPANY_PHONE,
	COMPANY_POSTAL_CODE,
	COMPANY_STREET,
	GESCHAEFTSFUEHRER,
	legalName,
} from '../consts'

const DIALOG_WIDTH = '64rem'

const GREETING = 'Sehr geehrte Damen und Herren,'

// So lange nach dem letzten Tastendruck wird die Vorschau mit dem neuen Text erzeugt.
const TEXT_PREVIEW_DELAY_MS = 400

const sxAuthority = {
	p: '0.75rem 1rem',
	m: '0 0 1rem 0',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
} as const

// Links die Mail so breit wie im Postfach, rechts Amt und Felder; wie im Mail-Dialog der Restaurants.
const sxColumns = { display: 'flex', gap: '1.5rem', alignItems: 'stretch' } as const

const sxSide = { flex: '0 0 20rem', height: '70vh', overflowY: 'auto', pt: '0.5rem' } as const

const sxPreview = {
	display: 'block',
	flex: '1',
	minWidth: '0',
	height: '70vh',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
} as const

// Erst mit geladenem Amt, damit das Mailfeld dessen Adresse als Startwert übernimmt.
const GewerbeauskunftForm = ({
	restaurant,
	authority,
	dismissPortal,
}: {
	restaurant: Restaurant
	authority: Gewerbeamt | null
	dismissPortal: () => void
}): JSX.Element => {
	const address = [
		[restaurant.street, restaurant.houseNumber].filter(Boolean).join(' '),
		[restaurant.zipCode, restaurant.city].filter(Boolean).join(' '),
	]
	const [email, setEmail] = React.useState(authority?.email ?? '')
	const [subject, setSubject] = React.useState(
		`Antrag auf einfache Gewerberegisterauskunft – ${restaurant.title}, ${address.join(', ')}`
	)
	const [greeting, setGreeting] = React.useState(GREETING)
	const [text, setText] = React.useState(
		`hiermit beantragen wir eine einfache Auskunft aus dem Gewerberegister nach § 14 Abs. 5 GewO zu folgendem Betrieb:

${restaurant.title}
${address.join('\n')}

Bitte teilen Sie uns Name des Gewerbetreibenden und angezeigte Tätigkeit mit. Sollte für den Betrieb kein Gewerbe (mehr) angemeldet sein, bitten wir um entsprechende Mitteilung. Wir benötigen die Angaben zur Prüfung einer geplanten Geschäftsbeziehung mit dem Betrieb.

Die anfallenden Gebühren übernehmen wir. Bitte senden Sie Auskunft und Gebührenbescheid an:

${legalName}, vertreten durch den Geschäftsführer ${GESCHAEFTSFUEHRER}
${COMPANY_STREET}, ${COMPANY_POSTAL_CODE} ${COMPANY_CITY}
${COMPANY_EMAIL} · ${COMPANY_PHONE}`
	)
	const [sending, setSending] = React.useState(false)
	const [sentTo, setSentTo] = React.useState<string | null>(null)
	const [previewHtml, setPreviewHtml] = React.useState<string | null>(null)

	// Die Vorschau kommt aus derselben API wie der Versand, also im üblichen Mail-Wrapper. Eine
	// überholte Antwort ersetzt die neuere nicht mehr.
	React.useEffect(() => {
		let outdated = false
		const timeout = setTimeout(async () => {
			const response = await createRestaurantMessage({
				settlementIds: [],
				format: 'EMAIL',
				content: 'FREE_TEXT',
				subject,
				greeting,
				intro: text,
			})
			if (outdated) {
				return
			} else if (response.success) {
				setPreviewHtml(response.message.html)
			} else {
				handleMutationError('createRestaurantMessage', response.errors)
			}
		}, TEXT_PREVIEW_DELAY_MS)
		return () => {
			outdated = true
			clearTimeout(timeout)
		}
	}, [greeting, subject, text])

	const handleSend = React.useCallback(async () => {
		setSending(true)
		// Mit Slug steht der Antrag danach in der Kommunikation des Restaurants.
		const response = await sendMessage({
			settlementIds: [],
			slugs: [restaurant.slug ?? null],
			sandbox: false,
			format: 'EMAIL',
			content: 'FREE_TEXT',
			email: email.trim(),
			subject,
			greeting,
			intro: text,
		})
		setSending(false)
		if (!response.success) {
			handleMutationError('sendMessage', response.errors)
		} else if (response.results[0]?.error) {
			handleMutationError('sendMessage', response.results[0].error)
		} else {
			setSentTo(email.trim())
		}
	}, [email, greeting, restaurant.slug, subject, text])

	return (
		<>
			<Box sx={sxColumns}>
				<Box
					component="iframe"
					sx={sxPreview}
					srcDoc={previewHtml ?? undefined}
					title="Vorschau der Mail"
				/>
				<Box sx={sxSide}>
			<Box sx={sxAuthority}>
				{authority ? (
					<>
						<Txt variant="subtitle2">{authority.name}</Txt>
						<Txt variant="body2" color="text.secondary">
							{[
								authority.department,
								authority.street,
								[authority.zipCode, authority.city].filter(Boolean).join(' '),
							]
								.filter(Boolean)
								.join(' · ')}
						</Txt>
						{authority.note ? (
							<Txt variant="body2" color="text.secondary" m="0.25rem 0 0 0">
								{authority.note}
							</Txt>
						) : null}
						{authority.sourceUrl ? (
							<Link variant="body2" href={authority.sourceUrl} target="_blank" rel="noreferrer">
								Quelle
							</Link>
						) : null}
					</>
				) : (
					<Txt variant="body2" color="text.secondary">
						Zur Anschrift des Restaurants ist kein Gewerbeamt hinterlegt. Bitte die Adresse des
						zuständigen Amts selbst eintragen.
					</Txt>
				)}
			</Box>
			<TextField label="E-Mail des Amts" defaultValue={email} onChange={setEmail} m="0 0 1rem 0" />
			<TextField label="Betreff" defaultValue={subject} onChange={setSubject} m="0 0 1rem 0" />
			<TextField label="Anrede" defaultValue={greeting} onChange={setGreeting} m="0 0 1rem 0" />
			<TextField
				label="Text"
				defaultValue={text}
				onChange={setText}
				multiline
				maxRows={20}
				m="0 0 1rem 0"
			/>
			{sentTo ? (
				<Txt variant="body2" color="success.main">
					Gesendet an {sentTo}.
				</Txt>
			) : null}
				</Box>
			</Box>
			<DialogButtonRow>
				<Button
					variant="mainButton"
					onClick={handleSend}
					loading={sending}
					disabled={!email.trim() || !subject.trim() || !text.trim() || sentTo !== null}
				>
					Senden
				</Button>
				<Button m="0 0 0 1rem" onClick={dismissPortal}>
					Schließen
				</Button>
			</DialogButtonRow>
		</>
	)
}

const GewerbeauskunftDialog = ({
	restaurant,
	dismissPortal,
}: {
	restaurant: Restaurant
	dismissPortal: () => void
}): JSX.Element => {
	const { gewerbeamt, loading } = useGewerbeamt(restaurant.id)

	return (
		<Box p="1.5rem" width={DIALOG_WIDTH} maxWidth="100%">
			<Txt fontWeight="bold" variant="h5" m="0 0 1rem 0">
				Gewerberegisterauskunft zu {restaurant.title}
			</Txt>
			{loading ? (
				<Txt variant="body2" color="text.secondary">
					Gewerbeamt wird gesucht …
				</Txt>
			) : (
				<GewerbeauskunftForm
					restaurant={restaurant}
					authority={gewerbeamt ?? null}
					dismissPortal={dismissPortal}
				/>
			)}
		</Box>
	)
}

// Antrag auf einfache Gewerberegisterauskunft per Mail an das Amt zur Anschrift des Restaurants.
export const renderGewerbeauskunftDialog = (restaurant: Restaurant): void =>
	Dialog.render({
		width: DIALOG_WIDTH,
		isLocked: true,
		renderContent: ({ dismissPortal }) => (
			<GewerbeauskunftDialog restaurant={restaurant} dismissPortal={dismissPortal} />
		),
	})
